import { useState, useCallback, useEffect } from 'react';
import { useCases } from '../../infrastructure/di/container';
import { TelegramAccountBrandingEntity } from '../../domain/entities/TelegramAccountBranding';
import { UpdateTelegramAccountBrandingInput } from '../../shared/types/TelegramAccountBranding';

const toErrorKey = (e: any): string =>
  e?.response?.data?.slug
  || (typeof e?.message === 'string' ? e.message.replace(/^Error:\s*/, '') : '')
  || 'unknown-error';

export function useTelegramAccountBranding(uuid: string) {
  const [branding, setBranding] = useState<TelegramAccountBrandingEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBranding(await useCases.getTelegramAccountBranding().execute(uuid));
    } catch (e: any) {
      setError(toErrorKey(e));
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (input: UpdateTelegramAccountBrandingInput) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await useCases.updateTelegramAccountBranding().execute(uuid, input);
      setBranding(updated);
      return true;
    } catch (e: any) {
      setError(toErrorKey(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [uuid]);

  return { branding, loading, saving, error, reload: load, save };
}
